import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchThemeReferenceFiles,
  createThemeReferenceFile,
  updateThemeReferenceFile,
  deleteThemeReferenceFile,
  parseReferencePages,
  type ThemeReferenceFileRecord,
} from '@/lib/themeReferenceFiles';
import ReferenceFileModal from '@/pages/superadmin/themes/components/ReferenceFileModal';
import { downloadZifekTagsDoc } from '@/lib/themeTagsDoc';

export default function ThemeReferenceFilesPage() {
  const navigate = useNavigate();

  const [files, setFiles] = useState<ThemeReferenceFileRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<ThemeReferenceFileRecord | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [preview, setPreview] = useState<ThemeReferenceFileRecord | null>(null);
  const [seedDraft, setSeedDraft] = useState<{ titre: string; description: string; contenu: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchThemeReferenceFiles();
      setFiles(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return files;
    return files.filter((f) => f.titre.toLowerCase().includes(q) || (f.description || '').toLowerCase().includes(q));
  }, [files, search]);

  const handleSave = async (input: { titre: string; description: string; contenu: string }) => {
    if (editing) {
      await updateThemeReferenceFile(editing.id, input);
    } else {
      await createThemeReferenceFile(input);
    }
    setShowModal(false);
    setEditing(null);
    await load();
  };

  const handleDelete = async (id: number) => {
    if (deletingId !== id) {
      setDeletingId(id);
      setTimeout(() => setDeletingId((prev) => (prev === id ? null : prev)), 2500);
      return;
    }
    setDeletingId(null);
    try {
      await deleteThemeReferenceFile(id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de suppression');
    }
  };

  /** Importe un fichier .md (ou .txt) et ouvre le modal pré-rempli. */
  const handleImportMd = async (file: File) => {
    try {
      const text = await file.text();
      const base = file.name.replace(/\.(md|markdown|txt)$/i, '');
      setEditing(null);
      setSeedDraft({ titre: base, description: `Importé depuis ${file.name}`, contenu: text });
      setShowModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de lire le fichier');
    }
  };

  const formatDate = (iso: string): string => {
    try {
      return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return '';
    }
  };

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <button
            onClick={() => navigate('/superadmin/themes')}
            className="flex items-center gap-1.5 text-xs text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer mb-2"
          >
            <i className="ri-arrow-left-line"></i>
            Retour aux thèmes
          </button>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Fichiers de référence</h2>
          <p className="text-sm text-foreground-500 mt-1 max-w-2xl">
            Bibliothèque de fichiers de référence : chaque fichier contient les instructions envoyées à l&apos;IA (et la liste des pages attendues) lors de la génération ou de la conversion d&apos;un thème.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/superadmin/themes/categories')}
            className="flex items-center gap-2 px-4 py-2.5 bg-background-100 border border-background-200/70 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap hover:bg-background-200/70 hover:text-foreground-900 transition-colors cursor-pointer"
          >
            <i className="ri-folder-line"></i>
            Catégories
          </button>
          <button
            onClick={downloadZifekTagsDoc}
            className="flex items-center gap-2 px-4 py-2.5 bg-background-100 border border-background-200/70 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap hover:bg-background-200/70 hover:text-foreground-900 transition-colors cursor-pointer"
            title="Télécharger la documentation complète des balises Zifek (.md)"
          >
            <i className="ri-file-download-line"></i>
            Exporter la doc .md
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 px-4 py-2.5 bg-background-100 border border-background-200/70 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap hover:bg-background-200/70 hover:text-foreground-900 transition-colors cursor-pointer"
            title="Créer une référence depuis un document .md téléversé"
          >
            <i className="ri-upload-2-line"></i>
            Importer un .md
          </button>
          <button
            onClick={() => { setEditing(null); setSeedDraft(null); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2.5 bg-foreground-950 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer"
          >
            <i className="ri-add-line"></i>
            Nouveau fichier de référence
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 px-3 py-2.5 rounded-md bg-red-50 border border-red-100 flex items-start gap-2">
          <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0 mt-0.5"></i>
          <span className="text-xs text-red-700">{error}</span>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-5 max-w-sm">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un fichier de référence..."
          className="w-full h-10 pl-9 pr-3 rounded-md border border-background-200/70 bg-background-100/50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-foreground-400 text-2xl"></i>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-background-300/60 rounded-lg bg-background-100/30">
          <i className="ri-book-open-line text-4xl text-foreground-300 block mb-3"></i>
          <p className="text-sm text-foreground-600 font-medium">Aucun fichier de référence</p>
          <p className="text-xs text-foreground-400 mt-1">Crée ton premier fichier via le bouton ci-dessus</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((file) => {
            const pages = parseReferencePages(file.contenu);
            return (
              <div key={file.id} className="bg-background-50 border border-background-200/70 rounded-lg p-4 hover:border-background-300/60 transition-colors flex flex-col">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <i className="ri-book-open-line text-foreground-400 flex-shrink-0"></i>
                    <h4 className="text-sm font-semibold text-foreground-900 truncate">{file.titre}</h4>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 ${pages.length > 0 ? 'bg-accent-100 text-accent-700' : 'bg-background-100 text-foreground-400'}`}>
                    {pages.length > 0 ? `${pages.length} pages` : '—'}
                  </span>
                </div>

                <p className="text-xs text-foreground-500 line-clamp-2 mb-3 flex-1">
                  {file.description || 'Aucune description'}
                </p>

                <div className="flex items-center justify-between text-[10px] text-foreground-400 mb-3">
                  <span className="font-mono">{file.contenu.length.toLocaleString()} car.</span>
                  <span>{formatDate(file.created_at)}</span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPreview(file)}
                    className="flex-1 h-8 rounded-md bg-background-100 hover:bg-background-200/70 border border-background-200/70 flex items-center justify-center gap-1.5 text-[11px] font-medium text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer"
                  >
                    <i className="ri-eye-line text-xs"></i>
                    Voir
                  </button>
                  <button
                    onClick={() => { setEditing(file); setShowModal(true); }}
                    className="w-8 h-8 rounded-md bg-background-100 hover:bg-background-200/70 border border-background-200/70 flex items-center justify-center transition-colors cursor-pointer"
                    title="Modifier"
                  >
                    <i className="ri-edit-line text-xs text-foreground-600"></i>
                  </button>
                  <button
                    onClick={() => handleDelete(file.id)}
                    className={`h-8 rounded-md flex items-center justify-center transition-colors cursor-pointer border ${
                      deletingId === file.id
                        ? 'bg-red-500 border-red-500 text-white px-2.5'
                        : 'w-8 bg-background-100 border-background-200/70 hover:bg-red-50 text-red-500'
                    }`}
                    title={deletingId === file.id ? 'Confirmer' : 'Supprimer'}
                  >
                    <i className={deletingId === file.id ? 'ri-check-line text-xs' : 'ri-delete-bin-line text-xs'}></i>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Input fichier caché pour l'import .md */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".md,.markdown,.txt,text/markdown,text/plain"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleImportMd(f);
          e.target.value = '';
        }}
      />

      {/* Modal création / édition */}
      {showModal && (
        <ReferenceFileModal
          initial={editing}
          seedDraft={seedDraft}
          onClose={() => { setShowModal(false); setEditing(null); setSeedDraft(null); }}
          onSave={handleSave}
        />
      )}

      {/* Modal aperçu */}
      {preview && (
        <>
          <div className="fixed inset-0 bg-black/30 z-50" onClick={() => setPreview(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4" onClick={() => setPreview(null)}>
            <div
              className="w-full max-w-3xl max-h-[88vh] bg-background-50 rounded-lg border border-background-200/70 shadow-xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-background-200/70 flex-shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                  <i className="ri-book-open-line text-foreground-500 flex-shrink-0"></i>
                  <h3 className="text-sm font-semibold text-foreground-950 truncate">{preview.titre}</h3>
                </div>
                <button
                  onClick={() => setPreview(null)}
                  className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
                >
                  <i className="ri-close-line text-foreground-500"></i>
                </button>
              </div>
              {preview.description && (
                <p className="px-5 py-2 text-xs text-foreground-500 border-b border-background-200/70">{preview.description}</p>
              )}
              <pre className="flex-1 overflow-auto p-5 text-[11px] text-foreground-700 font-mono leading-relaxed whitespace-pre-wrap bg-background-100/40">
                {preview.contenu || '(vide)'}
              </pre>
            </div>
          </div>
        </>
      )}
    </div>
  );
}